import React, { useEffect, useRef, useMemo } from "react";
import maplibregl from "@/lib/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { X, Route as RouteIcon, MapPin } from "lucide-react";
import { DARK_MAP_STYLE, normalizeLocationMap, applyDarkTheme, midpointOfPolyline, polylineMeters, formatDistance, createPinElement, projectOrigin, boundsOf, haversineMeters } from "@/lib/mapStyle";

/**
 * LocationMap — Phase 6 public viewer dark location map.
 *
 * Full-screen cinematic overlay. Renders the project's saved dark map with
 * landmark pins and routes; animated routes draw themselves in progressively
 * on open. Read-only.
 *
 * Props: { locationMap, onClose, lang }
 */
export default function LocationMap({ locationMap, onClose }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const rafRef = useRef(null);
  const markersRef = useRef([]);
  const cfg = useMemo(() => normalizeLocationMap(locationMap), [locationMap]);
  const origin = useMemo(() => projectOrigin(cfg), [cfg]);

  const fitRoute = (coords) => {
    const b = boundsOf({ landmarks: [], routes: [{ coords }] });
    if (b) { try { mapRef.current?.fitBounds(b, { padding: 100, duration: 800, maxZoom: 16 }); } catch (_) {} }
  };

  // Go to the admin-saved default camera (if one was set), else gently focus
  // the project itself. This is what a "project" tap does — NOT fit-all.
  const goToDefault = () => {
    const map = mapRef.current;
    if (!map) return;
    if (cfg.has_default_view) {
      try { map.flyTo({ center: cfg.center, zoom: cfg.zoom, pitch: cfg.pitch || 0, bearing: cfg.bearing || 0, duration: 800 }); } catch (_) {}
    } else if (origin) {
      try { map.flyTo({ center: origin, zoom: 15, duration: 800 }); } catch (_) {}
    }
  };

  // Tapping a landmark ALWAYS frames that landmark together with the project so
  // both are on screen. Tapping the project just returns to the default view.
  const focusLandmark = (lm) => {
    const map = mapRef.current;
    if (!map) return;
    if (lm.category === "project") { goToDefault(); return; }
    if (!origin) { try { map.flyTo({ center: [lm.lng, lm.lat], zoom: 15, duration: 800 }); } catch (_) {} return; }
    const b = boundsOf({ landmarks: [], routes: [{ coords: [origin, [lm.lng, lm.lat]] }] });
    if (b) { try { map.fitBounds(b, { padding: { top: 110, bottom: 150, left: 90, right: 90 }, duration: 800, maxZoom: 16 }); } catch (_) {} }
  };

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DARK_MAP_STYLE,
      center: cfg.center,
      zoom: cfg.zoom,
      pitch: cfg.pitch || 0,
      bearing: cfg.bearing || 0,
      attributionControl: true,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: true }), "top-right");
    mapRef.current = map;

    // The overlay mounts full-screen but MapLibre may read the container size
    // before layout settles → blank/short canvas. Force resize on load, after
    // paint, and whenever the container box changes.
    const doResize = () => { try { map.resize(); } catch (_) {} };
    const ro = new ResizeObserver(doResize);
    try { ro.observe(containerRef.current); } catch (_) {}
    const t1 = setTimeout(doResize, 60);
    const t2 = setTimeout(doResize, 300);
    requestAnimationFrame(doResize);

    map.on("load", () => {
      doResize();
      applyDarkTheme(map); // white roads, grey buildings, black background
      // Routes source + glow + main line.
      map.addSource("routes", { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "routes-glow", type: "line", source: "routes",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 10, "line-opacity": 0.18, "line-blur": 6 },
      });
      map.addLayer({
        id: "routes-line", type: "line", source: "routes",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 3.5, "line-opacity": 0.95 },
      });

      // Traveling glow "comet" that runs continuously along each route for a
      // premium cinematic feel (a blurred halo + a bright core dot).
      map.addSource("route-pulse", { type: "geojson", data: emptyFC() });
      map.addLayer({
        id: "route-pulse-glow", type: "circle", source: "route-pulse",
        paint: { "circle-radius": 15, "circle-color": ["get", "color"], "circle-opacity": 0.28, "circle-blur": 1 },
      });
      map.addLayer({
        id: "route-pulse-core", type: "circle", source: "route-pulse",
        paint: { "circle-radius": 4.5, "circle-color": "#ffffff", "circle-opacity": 0.95, "circle-blur": 0.2 },
      });

      // Distance labels pinned at each route's midpoint.
      map.addSource("route-labels", { type: "geojson", data: labelsFC(cfg.routes) });
      map.addLayer({
        id: "route-labels", type: "symbol", source: "route-labels",
        layout: {
          "text-field": ["get", "label"], "text-size": 13,
          "text-offset": [0, -0.8], "text-anchor": "bottom", "text-allow-overlap": true,
        },
        paint: { "text-color": "#ffffff", "text-halo-color": "#000000", "text-halo-width": 1.6 },
      });

      // Landmark markers (size + optional logo + distance-from-project sub-label).
      const origin = projectOrigin(cfg);
      markersRef.current = cfg.landmarks.map((lm) => {
        const isProject = lm.category === "project";
        const sublabel = isProject ? "" : formatDistance(haversineMeters(origin, [lm.lng, lm.lat]));
        const el = createPinElement(lm, { pulse: true, sublabel });
        // Keep the project pin above every other landmark, even when zoomed
        // out and pins overlap.
        el.style.zIndex = isProject ? "10000" : "1";
        // Tapping a pin frames it with the project (project pin → default view).
        el.style.cursor = "pointer";
        el.addEventListener("click", (ev) => { ev.stopPropagation(); focusLandmark(lm); });
        return new maplibregl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([lm.lng, lm.lat]).addTo(map);
      });

      // Honour an explicit saved "default view" (admin → Set default view). Only
      // auto-fit-bounds when the admin has NOT pinned a preferred camera.
      if (!cfg.has_default_view) {
        const b = boundsOf(cfg);
        if (b && (b[0][0] !== b[1][0] || b[0][1] !== b[1][1])) {
          try { map.fitBounds(b, { padding: { top: 90, bottom: 120, left: 90, right: 90 }, duration: 900, maxZoom: 16 }); } catch (_) {}
        }
      }

      // Prepare routes with cumulative lengths for the reveal animation.
      const routes = (cfg.routes || []).filter((r) => (r.coords || []).length >= 2).map((r) => ({
        ...r, ...precompute(r.coords),
      }));
      if (!routes.length) return;

      const startContinuous = () => {
        // One perpetual loop driving BOTH effects:
        //  1) marching dashes flowing start→end (stepped line-dasharray), and
        //  2) a glowing comet travelling along each route.
        let dstep = -1;
        const t0 = performance.now();
        const loop = (ts) => {
          const ns = Math.floor((ts / 45) % DASH_SEQUENCE.length);
          if (ns !== dstep) {
            dstep = ns;
            if (map.getLayer("routes-line")) {
              map.setPaintProperty("routes-line", "line-dasharray", DASH_SEQUENCE[ns]);
            }
          }
          const elapsed = ts - t0;
          const features = routes.map((r) => {
            const dur = Math.max(1600, (Number(r.duration_ms) || 3000) * 1.5);
            const p = (elapsed % dur) / dur; // 0→1, loops forever
            return {
              type: "Feature",
              properties: { color: r.color || "#C4A47C" },
              geometry: { type: "Point", coordinates: pointAt(r, p) },
            };
          });
          const psrc = map.getSource("route-pulse");
          if (psrc) psrc.setData({ type: "FeatureCollection", features });
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      };

      const anyAnimated = routes.some((r) => r.animate !== false);
      if (!anyAnimated) {
        map.getSource("routes").setData(fcFor(routes, () => 1));
        startContinuous();
        return;
      }

      const start = performance.now();
      const frame = (now) => {
        let done = true;
        const fc = fcFor(routes, (r) => {
          if (r.animate === false) return 1;
          const dur = Math.max(400, Number(r.duration_ms) || 3000);
          const t = Math.min(1, (now - start) / dur);
          if (t < 1) done = false;
          return easeInOut(t);
        });
        const src = map.getSource("routes");
        if (src) src.setData(fc);
        if (!done) {
          rafRef.current = requestAnimationFrame(frame);
        } else {
          // Reveal complete → lock in the full geometry, then hand off to the
          // perpetual dashed flow + travelling glow.
          if (src) src.setData(fcFor(routes, () => 1));
          startContinuous();
        }
      };
      rafRef.current = requestAnimationFrame(frame);
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(t1); clearTimeout(t2);
      try { ro.disconnect(); } catch (_) {}
      markersRef.current.forEach((m) => { try { m.remove(); } catch (_) {} });
      markersRef.current = [];
      try { map.remove(); } catch (_) {}
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationMap]);

  return (
    <div className="absolute inset-0 z-50 bg-black fade-in" data-testid="viewer-location-map">
      <div ref={containerRef} className="w-full h-full" style={{ width: "100%", height: "100%" }} />
      <button
        onClick={onClose}
        data-testid="location-map-close"
        className="absolute top-4 left-4 z-10 pill-nav inline-flex items-center gap-1.5 text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full hover:bg-white/10 transition"
        style={{ top: "max(16px, env(safe-area-inset-top))" }}
      >
        <X className="h-4 w-4" /> Close
      </button>

      {(cfg.routes.length > 0 || cfg.landmarks.length > 0) && (
        <div
          className="absolute bottom-4 left-4 z-10 max-w-[260px] rounded-2xl px-4 py-3 text-white pill-nav"
          data-testid="location-map-legend"
          style={{ bottom: "max(16px, env(safe-area-inset-bottom))" }}
        >
          {cfg.routes.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
                <RouteIcon className="h-3 w-3" /> Routes
              </div>
              <div className="space-y-1">
                {cfg.routes.map((r) => (
                  <button key={r.id} onClick={() => fitRoute(r.coords)}
                    className="flex items-center gap-2 text-xs w-full text-left hover:text-white/90" data-testid={`legend-route-${r.id}`}>
                    <span className="inline-block w-4 h-[3px] rounded-full" style={{ background: r.color || "#C4A47C" }} />
                    <span className="flex-1 truncate">{r.name}</span>
                    <span className="text-white/60 tabular-nums">{formatDistance(polylineMeters(r.coords))}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {cfg.landmarks.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
                <MapPin className="h-3 w-3" /> Landmarks
              </div>
              <div className="space-y-1">
                {cfg.landmarks.map((lm) => (
                  <button
                    key={lm.id}
                    onClick={() => focusLandmark(lm)}
                    className="flex items-center gap-2 text-xs w-full text-left hover:text-white/90"
                    data-testid={`legend-landmark-${lm.id}`}
                  >
                    <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: lm.color || "#C4A47C" }} />
                    <span className="flex-1 truncate">{lm.name}</span>
                    {lm.category !== "project" && (
                      <span className="text-white/60 tabular-nums">{formatDistance(haversineMeters(origin, [lm.lng, lm.lat]))}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Dash-array frames for the perpetual "flowing dashes" route animation. Each
// entry is [gap, dash, gap, dash...] in line-width units; stepping through them
// shifts the gap forward so the dashes appear to march from start → end.
const DASH_SEQUENCE = [
  [0, 4, 3],
  [0.5, 4, 2.5],
  [1, 4, 2],
  [1.5, 4, 1.5],
  [2, 4, 1],
  [2.5, 4, 0.5],
  [3, 4, 0],
  [0, 0.5, 3, 3.5],
  [0, 1, 3, 3],
  [0, 1.5, 3, 2.5],
  [0, 2, 3, 2],
  [0, 2.5, 3, 1.5],
  [0, 3, 3, 1],
  [0, 3.5, 3, 0.5],
];


// ── Route reveal helpers ────────────────────────────────────────────────
function precompute(coords) {
  const seg = [];
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const d = dist(coords[i - 1], coords[i]);
    seg.push(d); total += d;
  }
  return { _coords: coords, _seg: seg, _total: total || 1 };
}
// Point at fractional distance p (0→1) along a precomputed route.
function pointAt(r, p) {
  const t = Math.max(0, Math.min(1, p));
  if (t <= 0) return r._coords[0];
  if (t >= 1) return r._coords[r._coords.length - 1];
  const target = r._total * t;
  let acc = 0;
  for (let i = 1; i < r._coords.length; i++) {
    const d = r._seg[i - 1];
    if (acc + d >= target) {
      const k = (target - acc) / (d || 1);
      const a = r._coords[i - 1], b = r._coords[i];
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
    }
    acc += d;
  }
  return r._coords[r._coords.length - 1];
}

function sliceLine(r, t) {
  if (t >= 1) return r._coords;
  const target = r._total * t;
  const out = [r._coords[0]];
  let acc = 0;
  for (let i = 1; i < r._coords.length; i++) {
    const d = r._seg[i - 1];
    if (acc + d >= target) {
      const k = (target - acc) / (d || 1);
      const a = r._coords[i - 1], b = r._coords[i];
      out.push([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k]);
      break;
    }
    acc += d;
    out.push(r._coords[i]);
  }
  return out;
}
function fcFor(routes, progressFn) {
  return {
    type: "FeatureCollection",
    features: routes.map((r) => ({
      type: "Feature",
      properties: { color: r.color || "#C4A47C" },
      geometry: { type: "LineString", coordinates: sliceLine(r, progressFn(r)) },
    })),
  };
}
function dist(a, b) { const dx = a[0] - b[0], dy = a[1] - b[1]; return Math.sqrt(dx * dx + dy * dy); }
function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
function emptyFC() { return { type: "FeatureCollection", features: [] }; }
function labelsFC(routes) {
  return {
    type: "FeatureCollection",
    features: (routes || []).filter((r) => (r.coords || []).length >= 2).map((r) => ({
      type: "Feature",
      properties: { label: `${r.name} · ${formatDistance(polylineMeters(r.coords))}` },
      geometry: { type: "Point", coordinates: midpointOfPolyline(r.coords) },
    })),
  };
}
